import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Inbox, CalendarDays, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ENQUIRY_FLOW = ["new", "contacted", "closed"];
const BOOKING_FLOW = ["new", "confirmed", "completed"];

const statusCls = {
  new: "bg-primary/10 text-primary border-primary/30",
  contacted: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  confirmed: "bg-accent/10 text-accent border-accent/30",
  closed: "bg-white/5 text-muted-foreground border-white/10",
  completed: "bg-white/5 text-muted-foreground border-white/10",
};

function nextStatus(flow, current) {
  const i = flow.indexOf(current);
  return i >= 0 && i < flow.length - 1 ? flow[i + 1] : null;
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const navigate = useNavigate();

  const startMfaSetup = async () => {
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setMfaSetup(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "MFA setup failed.");
    }
  };

  const enableMfa = async () => {
    try {
      await api.post("/auth/mfa/enable", { code: mfaCode });
      setUser((u) => ({ ...u, mfa_enabled: true }));
      setMfaSetup(null);
      setMfaCode("");
      setMfaOpen(false);
      toast.success("MFA enabled. You'll need your authenticator app at next login.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code.");
    }
  };

  const load = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const [e, b] = await Promise.all([api.get("/enquiries"), api.get("/bookings")]);
      setEnquiries(e.data);
      setBookings(b.data);
    } catch {
      localStorage.removeItem("zt_token");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem("zt_token");
    navigate("/admin/login");
  };

  const advance = async (kind, item) => {
    const flow = kind === "enquiries" ? ENQUIRY_FLOW : BOOKING_FLOW;
    const next = nextStatus(flow, item.status);
    if (!next) return;
    try {
      await api.patch(`/${kind}/${item.id}/status`, { status: next });
      const setter = kind === "enquiries" ? setEnquiries : setBookings;
      setter((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
      toast.success(`Marked as ${next}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (kind, item) => {
    try {
      await api.delete(`/${kind}/${item.id}`);
      const setter = kind === "enquiries" ? setEnquiries : setBookings;
      setter((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground animate-pulse">Establishing secure channel...</p>
      </div>
    );
  }

  const items = tab === "enquiries" ? enquiries : bookings;
  const flow = tab === "enquiries" ? ENQUIRY_FLOW : BOOKING_FLOW;
  const newCount = (arr) => arr.filter((x) => x.status === "new").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="border-b border-white/10 bg-[#0D0E13]/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-dark.png" alt="ZTrust Africa" className="h-7 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Command</span>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-email" className="hidden sm:block font-mono text-xs text-muted-foreground">{user?.email}</span>
            <button
              onClick={() => { setMfaOpen((v) => !v); if (!mfaOpen && !user?.mfa_enabled && !mfaSetup) startMfaSetup(); }}
              data-testid="mfa-toggle-btn"
              className={`flex items-center gap-2 px-4 py-2 border text-sm transition-colors duration-300 ${user?.mfa_enabled ? "border-accent/40 text-accent" : "border-white/10 text-muted-foreground hover:text-primary hover:border-primary"}`}
            >
              <ShieldCheck className="w-4 h-4" /> {user?.mfa_enabled ? "MFA On" : "Enable MFA"}
            </button>
            <button onClick={load} data-testid="refresh-btn" aria-label="Refresh" className="p-2 border border-white/10 text-muted-foreground hover:text-primary hover:border-primary transition-colors duration-300">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 px-4 py-2 border border-white/10 text-sm text-muted-foreground hover:text-destructive hover:border-destructive transition-colors duration-300">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {mfaOpen && !user?.mfa_enabled && (
          <div data-testid="mfa-setup-panel" className="border border-primary/30 bg-[#151821] p-6 sm:p-8 mb-10">
            <h2 className="font-heading font-bold tracking-tight text-xl mb-2">Set up two-factor authentication</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-lg">
              Scan this QR code with Google Authenticator, Microsoft Authenticator, or Authy. Then enter the 6-digit code it shows to finish.
            </p>
            {mfaSetup ? (
              <div className="flex flex-col sm:flex-row gap-8 items-start">
                <img src={mfaSetup.qr} alt="MFA QR code" data-testid="mfa-qr-image" className="w-44 h-44 border border-white/10 bg-white p-2" />
                <div className="flex-1 w-full">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Manual entry key</p>
                  <p data-testid="mfa-secret" className="font-mono text-sm text-accent break-all mb-6">{mfaSetup.secret}</p>
                  <div className="flex gap-3">
                    <input
                      inputMode="numeric"
                      data-testid="mfa-enable-code-input"
                      placeholder="6-digit code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="flex-1 bg-[#0D0E13] border border-white/10 text-white text-sm px-4 py-3 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary font-mono tracking-[0.3em]"
                    />
                    <button onClick={enableMfa} data-testid="mfa-enable-submit-btn" className="bg-primary text-background font-bold px-6 py-3 text-sm hover:bg-[#7AD6E6] transition-colors duration-300">
                      Enable
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="font-mono text-xs text-muted-foreground animate-pulse">Generating secure key...</p>
            )}
          </div>
        )}
        {mfaOpen && user?.mfa_enabled && (
          <div data-testid="mfa-status-panel" className="border border-accent/30 bg-[#151821] p-6 mb-10">
            <p className="text-sm text-accent font-mono tracking-wide">Two-factor authentication is active on this account.</p>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="stats-row">
          {[
            { icon: Inbox, label: "Enquiries", value: enquiries.length },
            { icon: Inbox, label: "New Enquiries", value: newCount(enquiries) },
            { icon: CalendarDays, label: "Bookings", value: bookings.length },
            { icon: CalendarDays, label: "New Bookings", value: newCount(bookings) },
          ].map((s) => (
            <div key={s.label} className="border border-white/10 bg-[#151821] p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <s.icon className="w-4 h-4 text-primary mb-3" />
              <p className="font-heading font-extrabold text-3xl">{s.value}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-0 border border-white/10 w-max mb-6" role="tablist">
          {["enquiries", "bookings"].map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${tab === t ? "bg-primary text-background font-bold" : "text-muted-foreground hover:text-white"}`}
            >
              {t} ({t === "enquiries" ? enquiries.length : bookings.length})
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div data-testid="empty-state" className="border border-white/10 bg-[#151821] p-16 text-center">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">No {tab} yet. Standing by.</p>
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid={`${tab}-list`}>
            {items.map((item) => {
              const next = nextStatus(flow, item.status);
              return (
                <article key={item.id} data-testid={`${tab}-item-${item.id}`} className="bg-[#151821] p-5 sm:p-6 flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-heading font-bold tracking-tight">{item.name}</h3>
                      <span className={`font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 border ${statusCls[item.status] || statusCls.new}`} data-testid={`status-${item.id}`}>
                        {item.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <a href={`mailto:${item.email}`} className="hover:text-primary transition-colors duration-200">{item.email}</a>
                      {item.company ? ` — ${item.company}` : ""}
                    </p>
                    {tab === "bookings" ? (
                      <p className="font-mono text-xs text-accent mt-2">
                        {item.service} // {item.preferred_date} @ {item.preferred_time}
                      </p>
                    ) : null}
                    <p className="text-sm text-foreground/80 mt-3 whitespace-pre-wrap">{tab === "bookings" ? item.notes || "(no notes)" : item.message}</p>
                    <p className="font-mono text-[10px] text-muted-foreground/60 mt-3">
                      {new Date(item.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex lg:flex-col gap-2 shrink-0">
                    {next && (
                      <button
                        onClick={() => advance(tab, item)}
                        data-testid={`advance-${item.id}`}
                        className="px-4 py-2 text-xs font-bold border border-primary/40 text-primary hover:bg-primary hover:text-background transition-colors duration-300"
                      >
                        Mark {next}
                      </button>
                    )}
                    <button
                      onClick={() => remove(tab, item)}
                      data-testid={`delete-${item.id}`}
                      aria-label="Delete"
                      className="px-4 py-2 text-xs border border-white/10 text-muted-foreground hover:text-destructive hover:border-destructive transition-colors duration-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
